/**
 * Usage Credit (legacy "Nutzungsguthaben") models — EB-3883.
 *
 * Credits live in the invoice-api credit-controller (`/invoice/v1/credits`),
 * separate from the BILLING table the rest of the billing UI uses. A credit is
 * bound to the customer (RAV_ID), not to a contract/UA/PS (BR-3).
 */

/**
 * A single usage-credit row as returned by `GET /invoice/v1/credits`.
 * Field names mirror the API JSON exactly.
 *
 * Note: `customerId` is a **string** in API responses (BR-4 / API notes),
 * even though the rest of the app keys customers by numeric id.
 */
export interface Credit {
  id: number;
  customerId: string;
  type: number;
  amount: number;
  /** Creation timestamp (Berlin local, date-time). */
  insertedOn: string;
  /** Nullable expiry date — `null` means "no date" (open-ended). */
  validUntil: string | null;
  reason: string;
  /** FK to invoice.id once the credit has been invoiced; `null` while open. */
  invoiceId: number | null;
  employeeId: number;
}

/** Payload for `POST /invoice/v1/credits`. */
export interface CreateCreditPayload {
  /** Customer id as a string (API contract — BR-4). */
  customerId: string;
  type: number;
  amount: number;
  /** `null` = "no date" (open-ended validity). */
  validUntil: string | null;
  reason: string;
  /** Logged-in agent's user id (`authService.currentUserId`) — never displayed. */
  employeeId: number;
}

/** "Guthaben, allgemein" — a general credit (positive amounts). */
export const CREDIT_TYPE_GENERAL = 0;

/** "Guthaben, verfallen" — an expired credit (negative amounts). */
export const CREDIT_TYPE_EXPIRED = 2;

/**
 * Map a credit `type` to its English UI label.
 *
 * BR-2: there is NO type-1 concept anymore — historical rows with other type
 * values still exist in the data (e.g. credit 347590, type 1). They fall
 * through to the raw-number fallback (rendered muted) — no dedicated label,
 * no filtering, never creatable.
 */
export function creditTypeLabel(type: number): string {
  switch (type) {
    case CREDIT_TYPE_GENERAL:
      return 'General';
    case CREDIT_TYPE_EXPIRED:
      return 'Expired';
    default:
      return String(type);
  }
}
